import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ProjectedVolumeDataPoint } from '../model';

interface ChartProps {
  primaryGradingVolumeTotal?: number;
  projectedPrimaryGradingVolume?: ProjectedVolumeDataPoint[];
}

const Chart: React.FC<ChartProps> = ({
  primaryGradingVolumeTotal = 0,
  projectedPrimaryGradingVolume = []
}) => {
  const currentMode = useSelector((state: RootState) => state.theme.currentMode);
  const isDark = currentMode === 'dark';

  // Theme-aware chart colors
  const CHART_COLORS = {
    recommended: isDark ? '#E5E7EB' : '#1d1d1d',
    overrides: isDark ? '#B794F4' : '#7B4FB5',
    grid: isDark ? 'rgba(255,255,255,0.2)' : '#e0e0e0',
    tick: isDark ? '#E5E7EB' : '#666',
    legend: isDark ? '#E5E7EB' : '#374151',
    tooltipBg: isDark ? '#1e1e32' : 'white',
    tooltipBorder: isDark ? 'rgba(255,255,255,0.15)' : '#e0e0e0',
  };

  // Transform projected volume data
  const data = React.useMemo(() => {
      if (!projectedPrimaryGradingVolume?.length || !primaryGradingVolumeTotal) {
        return [];
      }

      // Data is cumulative: point.recommended = total volume processed up to this hour.
      // Plot cumulative values directly so the chart shows an increasing trend from 0 → total.
      const transformedData = projectedPrimaryGradingVolume.map((point: ProjectedVolumeDataPoint) => {
      const remainingVolume = Math.max(0, primaryGradingVolumeTotal - point.recommended);

      return {
        time: point.time,
        Recommended: point.recommended,
        RemainingVolume: remainingVolume,
        Overrides: (point as any).overrides ?? null,
      };
    });

    return transformedData;
  }, [projectedPrimaryGradingVolume, primaryGradingVolumeTotal]);

  // Check if any override data exists
  const hasOverrides = data.some(d => d.Overrides !== null && d.Overrides !== undefined);

  return (
    <div style={{ width: '100%', height: 400 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{
            top: 40,
            right: 30,
            bottom: 60
          }}
        >
          <CartesianGrid strokeDasharray="1 1" stroke={CHART_COLORS.grid} />
          <XAxis 
            dataKey="time" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            domain={[0, 'auto']}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: CHART_COLORS.tooltipBg,
              border: `1px solid ${CHART_COLORS.tooltipBorder}`,
              borderRadius: '4px',
              color: CHART_COLORS.tick,
            }}
          />
          
          {/* Recommended line - cumulative volume processed */}
          <Line
            type="monotone"
            dataKey="Recommended"
            stroke={CHART_COLORS.recommended}
            strokeWidth={3}
            dot={{ fill: CHART_COLORS.recommended, r: 4 }}
            activeDot={{ r: 6, fill: CHART_COLORS.recommended }}
            name="Recommended"
          />

          {/* Overrides line (theme primary purple) */}
          {hasOverrides && (
            <Line
              type="monotone"
              dataKey="Overrides"
              stroke={CHART_COLORS.overrides}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: CHART_COLORS.overrides, r: 3 }}
              activeDot={{ r: 5, fill: CHART_COLORS.overrides }}
              name="Overrides"
              connectNulls={false}
            />
          )}
          
          {/* Legend with theme-aware text */}
          <Legend 
            verticalAlign="bottom"
            height={36}
            iconType="circle"
            formatter={(value: string) => <span style={{ color: CHART_COLORS.legend }}>{value}</span>}
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px'
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Chart;
